import {
  COMMENT_KINDS,
  dateLong,
  type CommentKind,
  type OpsChantier,
  type OpsCommentDto,
} from '@cockpit/shared';
import { Badge, Button, ErrorState, Input, Spinner, cn } from '@cockpit/ui';
import { useState } from 'react';
import { ApiError } from '../../lib/api.js';
import { useActionDone, useAddComment } from '../../lib/queries.js';
import { Section } from './parts.js';

const addDays = (iso: string, n: number) =>
  new Date(Date.parse(iso) + n * 86_400_000).toISOString().slice(0, 10);
const KIND_TONE = { Décision: 'navy', Action: 'azur', Commentaire: 'neutral' } as const;

/** Décisions, actions et commentaires du chantier : partagés entre tous les utilisateurs autorisés. */
export function FicheComments({
  r,
  asOf,
  list,
  loading,
  error,
}: {
  r: OpsChantier;
  asOf: string;
  list: OpsCommentDto[] | undefined;
  loading: boolean;
  error: Error | null;
}) {
  const add = useAddComment(r.code);
  const toggle = useActionDone(r.code);
  const [kind, setKind] = useState<CommentKind>('Décision');
  const [text, setText] = useState('');
  const [resp, setResp] = useState(r.cond ?? r.com ?? '');
  const [due, setDue] = useState(addDays(asOf, 14));
  const [msg, setMsg] = useState<string | null>(null);

  const save = () => {
    setMsg(null);
    add.mutate(
      { kind, text, ...(kind === 'Action' ? { resp, due } : {}) },
      {
        onSuccess: () => setText(''),
        onError: (e) => setMsg(e instanceof ApiError ? e.message : 'Enregistrement impossible, réessayez.'),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <Section
        title="Ajouter une décision, une action ou un commentaire"
        aside="visible par tous les utilisateurs de la revue"
      >
        <div role="group" aria-label="Type" className="flex gap-1.5">
          {COMMENT_KINDS.map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              className={cn(
                'rounded-pill border px-3 py-1 text-[13px]',
                kind === k
                  ? 'border-navy-600 bg-navy-600 text-on-accent'
                  : 'border-line bg-surface text-ink-2 hover:bg-surface-2',
              )}
            >
              {k}
            </button>
          ))}
        </div>
        {kind === 'Action' && (
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-[13px] text-ink-2">
              Responsable
              <Input value={resp} onChange={(e) => setResp(e.target.value)} className="h-9 w-48" />
            </label>
            <label className="flex items-center gap-2 text-[13px] text-ink-2">
              Échéance
              <Input
                type="date"
                value={due}
                min={asOf}
                onChange={(e) => setDue(e.target.value)}
                className="h-9 w-40"
              />
            </label>
          </div>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          maxLength={1000}
          aria-label="Texte"
          placeholder="Ex. : avenant demandé au client, point équipe prévu le 12, commande renégociée…"
          className="w-full rounded-sm border border-line bg-surface p-3 text-sm text-ink focus:border-azur-500 focus:outline-none"
        />
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={save} disabled={!text.trim() || add.isPending}>
            Enregistrer
          </Button>
          {msg && (
            <span role="alert" className="text-[13px] text-danger-700">
              {msg}
            </span>
          )}
        </div>
      </Section>

      <Section title="Historique" aside={list ? `${list.length}` : undefined}>
        {loading ? (
          <Spinner />
        ) : error ? (
          <ErrorState>{error.message}</ErrorState>
        ) : list?.length ? (
          <ol className="flex flex-col divide-y divide-line-soft">
            {list.map((c) => {
              const late = c.kind === 'Action' && !c.done && c.due != null && c.due < asOf;
              return (
                <li key={c.id} className="flex flex-col gap-1 py-3 first:pt-0">
                  <span className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-3">
                    <Badge tone={KIND_TONE[c.kind]}>{c.kind}</Badge>
                    {dateLong(c.createdAt.slice(0, 10))} · {c.author}
                  </span>
                  <p className="whitespace-pre-wrap text-[14px] text-ink">{c.text}</p>
                  {c.kind === 'Action' && (
                    <span className="flex flex-wrap items-center gap-3 text-[13px] text-ink-2">
                      <span>{c.resp || 'sans responsable'}</span>
                      <span className={cn(late && 'font-semibold text-danger-700')}>
                        échéance {c.due ? dateLong(c.due) : '—'}
                        {late && c.due
                          ? ` · en retard de ${Math.round((Date.parse(asOf) - Date.parse(c.due)) / 86_400_000)} j`
                          : ''}
                      </span>
                      <Button
                        size="sm"
                        variant={c.done ? 'secondary' : 'ghost'}
                        aria-pressed={c.done}
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate({ id: c.id, done: !c.done })}
                      >
                        {c.done ? `Fait${c.doneOn ? ` le ${dateLong(c.doneOn)}` : ''}` : 'Marquer comme fait'}
                      </Button>
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        ) : (
          <p className="text-[13.5px] text-ink-3">
            Aucun commentaire sur ce chantier. Les décisions prises en réunion avec le conducteur se notent
            ici, pour vérifier le mois suivant qu’elles ont porté leurs fruits.
          </p>
        )}
      </Section>
    </div>
  );
}
